"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.decodeEncryptedShareBlob = exports.decodeShareBlob = exports.encodeEncryptedShareBlob = exports.encodeShareBlob = void 0;
const gzip_1 = require("@jsonjoy.com/util/lib/compression/gzip");
const toBase64Url_1 = require("@jsonjoy.com/base64/lib/toBase64Url");
const fromBase64Url_1 = require("@jsonjoy.com/base64/lib/fromBase64Url");
const SALT_LEN = 16;
const IV_LEN = 12;
const PBKDF2_ITERATIONS = 200000;
const subtle = () => {
    const c = globalThis.crypto;
    if (!c?.subtle)
        throw new Error('NO_CRYPTO');
    return c.subtle;
};
const randomBytes = (length) => {
    const c = globalThis.crypto;
    if (!c?.getRandomValues)
        throw new Error('NO_CRYPTO');
    return c.getRandomValues(new Uint8Array(length));
};
const deriveKey = async (password, salt, usage) => {
    const baseKey = await subtle().importKey('raw', new TextEncoder().encode(password), { name: 'PBKDF2' }, false, [
        'deriveKey',
    ]);
    return subtle().deriveKey({ name: 'PBKDF2', salt: salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' }, baseKey, { name: 'AES-GCM', length: 256 }, false, usage);
};
/**
 * Encode the document binary as a base64url string suitable for embedding in
 * a URL. The bytes are gzip-compressed before encoding.
 */
const encodeShareBlob = async (bytes) => {
    const compressed = await (0, gzip_1.gzip)(bytes);
    return (0, toBase64Url_1.toBase64Url)(compressed, compressed.length);
};
exports.encodeShareBlob = encodeShareBlob;
/**
 * Encode the document binary as an encrypted, base64url-encoded string.
 */
const encodeEncryptedShareBlob = async (bytes, password) => {
    if (!password)
        throw new Error('NO_PWD');
    const compressed = await (0, gzip_1.gzip)(bytes);
    const salt = randomBytes(SALT_LEN);
    const iv = randomBytes(IV_LEN);
    const key = await deriveKey(password, salt, ['encrypt']);
    const ct = new Uint8Array(await subtle().encrypt({ name: 'AES-GCM', iv: iv }, key, compressed));
    const out = new Uint8Array(SALT_LEN + IV_LEN + ct.length);
    out.set(salt, 0);
    out.set(iv, SALT_LEN);
    out.set(ct, SALT_LEN + IV_LEN);
    return (0, toBase64Url_1.toBase64Url)(out, out.length);
};
exports.encodeEncryptedShareBlob = encodeEncryptedShareBlob;
/**
 * Decode a plaintext share blob produced by {@link encodeShareBlob}.
 */
const decodeShareBlob = async (encoded) => {
    const compressed = (0, fromBase64Url_1.fromBase64Url)(encoded);
    return (await (0, gzip_1.ungzip)(compressed));
};
exports.decodeShareBlob = decodeShareBlob;
/**
 * Decode an encrypted share blob produced by {@link encodeEncryptedShareBlob}.
 */
const decodeEncryptedShareBlob = async (encoded, password) => {
    const blob = (0, fromBase64Url_1.fromBase64Url)(encoded);
    if (blob.length < SALT_LEN + IV_LEN + 16)
        throw new Error('Encrypted blob is too short to be valid.');
    const salt = blob.slice(0, SALT_LEN);
    const iv = blob.slice(SALT_LEN, SALT_LEN + IV_LEN);
    const ct = blob.slice(SALT_LEN + IV_LEN);
    const key = await deriveKey(password, salt, ['decrypt']);
    const compressed = new Uint8Array(await subtle().decrypt({ name: 'AES-GCM', iv: iv }, key, ct));
    return (await (0, gzip_1.ungzip)(compressed));
};
exports.decodeEncryptedShareBlob = decodeEncryptedShareBlob;
